/**
 * This file contains utility function that allow to convert 
 * DeviceStatisticsResponse to the GeoJSON geometries in various of differnet
 * ways.
 */
import { FeatureCollection, Feature, LineString, Position, BBox, Polygon } from "geojson";
import moment from "moment";
import { distance, destination, bearing, point } from "@turf/turf";

import { DeviceStatisticsResponse } from "@services/devicesService";
import { precision } from "@geobuf/precision";
import { cameraDumpThreshold } from "@src/utils/cameraDump";
import { turnRadiiMeters } from "@screens/TracksScreen/turnRadius";

/**
 * Where a track lost its points. Every stage that can silently discard a fix
 * writes its tally here, so a blank map can be read off one console line
 * instead of guessed at. Filled by `sensorFeatures` when a `stats` object is
 * passed; `points` is what actually survived into geometry.
 */
export type TrackDropStats = {
  /** Points in the API response. 0 = the backend returned nothing. */
  length: number;
  /** Device's admin-set `min_satellites_count`. */
  satFloor: number;
  /** True when the floor rejected every fix and was ignored (see below). */
  satFloorIgnored: boolean;
  /** Dropped at 0,0 / out-of-range coordinates. */
  invalidCoord: number;
  /** Dropped for reporting fewer satellites than the floor. */
  belowSatFloor: number;
  /** Dropped as > MAX_DISTANCE_FROM_MEDIAN_KM from the track's median center. */
  farFromMedian: number;
  /** Dropped as a GPS jump (> MAX_IMPLIED_SPEED_KMH from the last good fix). */
  speedJump: number;
  /** Points left after the delay shift — what geometry is built from. */
  points: number;
  /** Polygons handed to Mapbox. 0 with points > 0 means geometry collapsed. */
  features: number;
  /** Extent of the drawn track, metres corner to corner. */
  bboxDiagonalM: number;
  /** True when the <100 m stationary guard emptied the collection. */
  stationaryDropped: boolean;
  /** Header width (m) and delay (s) the polygons were built with. */
  widthM: number;
  delayS: number;
};

export const newDropStats = (): TrackDropStats => ({
  length: 0, satFloor: 0, satFloorIgnored: false,
  invalidCoord: 0, belowSatFloor: 0, farFromMedian: 0, speedJump: 0,
  points: 0, features: 0, bboxDiagonalM: 0, stationaryDropped: false,
  widthM: 0, delayS: 0,
});

// Fewest satellites a fix must report to be drawn. Per-device, from the
// backend (`min_satellites_count`, editable in the admin device dialog), and 0
// by default — some GPS modules deliver a valid position while reporting 0
// satellites, so a hard-coded floor blanked those machines' tracks even though
// every point had coordinates. Only the admin-set value filters now.
//
// The floor is admin-set, so it can sit above what a machine's GPS actually
// reports — and a device that sends no satellite count at all arrives here as
// 0 (the API emits null, the parser coerces it). Either way every fix fails the
// test, the customer's map goes blank with nothing to explain it, and the admin
// Metrics map still draws the same track (it substitutes satQty 99 and applies
// no floor), so the two views disagree about a device that is reporting fine.
// A floor that would leave nothing to draw is treated as misconfigured and
// ignored for this track.
const minSatellitesOf = (
  response: DeviceStatisticsResponse,
  stats?: TrackDropStats,
): number => {
  const floor = response.getMinSatellites();
  if (stats) stats.satFloor = floor;
  if (floor <= 0) return 0;

  const { decode } = precision(10);
  const satellites = response.getSatQtyList();
  const coords = response.getCoordsList();

  for (let i = 0; i < response.getLength(); i++) {
    if (satellites[i] < floor) continue;
    if (isValidCoord(decode(coords[i * 2]), decode(coords[i * 2 + 1]))) return floor;
  }

  if (stats) stats.satFloorIgnored = true;
  console.warn(
    `min_satellites_count=${floor} rejects every fix in this window — ` +
    "drawing the track unfiltered. Check the device's satellite reporting.",
  );
  return 0;
};
// Maximum interval (in secodes) between two point for them to be in the same line.
const MAX_DURATION_BETWEEN_POINTS = 5 * 60;
// Shortest interval between two fixes that counts as the machine stopping.
//
// The break threshold is normally `delay * 1.5` — ~24 s for the usual 16 s
// conveyor delay. But `delay` is a device setting and can legitimately be ~0
// ("don't shift the sensor against the GPS at all"), and a device carrying
// delay=1 ms gives a threshold of 0.0015 s. Point timestamps are whole seconds,
// so every single step then reads as a stop: the track is cut into one-point
// segments, each collapsing to a sliver, and the map looks empty while the
// bounding box still covers the whole field. A machine logging every 1-3 s has
// not stopped in under 10 s.
const MIN_SEGMENT_GAP_SECONDS = 10;
// Interval after which a step in the track is drawn as a break.
const segmentGapSeconds = (delay: number): number =>
  Math.max(delay * 1.5, MIN_SEGMENT_GAP_SECONDS);
// Maximum distance (in km) from the median center to consider a point valid.
// Points farther than this are treated as GPS outliers.
// Kept in step with MAX_DISTANCE_FROM_MEDIAN_KM in backend-ai/src/lib/geo.ts.
const MAX_DISTANCE_FROM_MEDIAN_KM = 15;
// Implied speed (km/h) between two consecutive fixes above which the later fix
// is a GPS jump rather than real travel.
const MAX_IMPLIED_SPEED_KMH = 120;
// ── Map value-layer ids ─────────────────────────────────────────────────────
// Declared here rather than in Screen.tsx so the Details window can reference
// them too without importing the screen that renders it.
export const SENSOR_LAYER = "sensor";
export const SPEED_LAYER = "speed";
// Potato-camera metric layers (gated by the account's cameraInstalled flag).
export const POTATO_LAYER = "potato";
export const NOT_POTATO_LAYER = "not_potato";
export const SIZE_LAYER = "size";
// Grain-moisture layer (gated on the machine actually reporting moisture).
export const MOISTURE_LAYER = "moisture";
// Fuel sensor input in mV (gated on the admin "fuel sensor connected" switch).
export const FUEL_LAYER = "fuel";
// Every value layer the layer-picker can switch between.
export const VALUE_LAYERS = [
  SENSOR_LAYER, SPEED_LAYER, POTATO_LAYER, NOT_POTATO_LAYER, SIZE_LAYER,
  MOISTURE_LAYER, FUEL_LAYER,
];
// Camera layers only — harvester-width fills colored by "metricColor".
export const CAMERA_LINE_LAYERS = [POTATO_LAYER, NOT_POTATO_LAYER, SIZE_LAYER];
// Every harvester-width fill colored by "metricColor". Moisture is built the
// same way as the camera metrics, so it shares their paint and their <Source>.
export const METRIC_FILL_LAYERS = [...CAMERA_LINE_LAYERS, MOISTURE_LAYER, FUEL_LAYER];

// Color pallet for sensor values
export const SENSOR_PALLET = [
  "#A50026",
  "#D73027",
  "#F46D43",
  "#FDAE61",
  "#F8E559",
  "#A6D96A",
  "#66BD63",
  "#1A9850",
  "#006837",
  "#252525",
];
// Qualitative palette used to tell apart tracks from different devices when
// several are plotted at once. Indexed by selection order.
export const DEVICE_COLORS = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#17becf",
  "#bcbd22",
  "#7f7f7f",
];

// deviceColorAt returns a stable color for a device given its position in the
// current selection.
export function deviceColorAt(index: number): string {
  return DEVICE_COLORS[((index % DEVICE_COLORS.length) + DEVICE_COLORS.length) % DEVICE_COLORS.length];
}

// Color pallet for speed valeus
const SPEED_PALLET = [
  "#1A9850",
  "#66BD63",
  "#A6D96A",
  "#F8E559",
  "#FDAE61",
  "#F46D43",
  "#D73027",
  "#A50026",
];

// ── Potato-camera scales & size buckets ─────────────────────────────────────
// Camera metrics are ABSOLUTE (counts / pixel size), so unlike the sensor's
// fixed 0..100 scale they are ramped across the selection's own data range.
// They reuse SENSOR_PALLET (red→green) so every value layer reads the same way.
// The stone ramp is reversed: lots of stones is bad, so it must read red.
const NOT_POTATO_PALLET = [...SENSOR_PALLET].reverse();

// Grain-moisture palette: the same drier→wetter ramp the satellite soil-moisture
// overlay uses (FieldsScreen/spectralLegend.ts), so "more blue = wetter" reads
// the same way on both maps.
export const MOISTURE_PALLET = [
  "#d6bc8c",
  "#c8c896",
  "#82c8c8",
  "#3c96d2",
  "#1450be",
];

// Fuel sensor palette: a single-hue amber ramp, low → high input voltage. Not
// red→green: until a tank is calibrated we do not know whether more mV means
// more fuel or less (that depends on how the sender is wired).
export const FUEL_PALLET = [
  "#fde68a",
  "#fbbf24",
  "#f59e0b",
  "#d97706",
  "#92400e",
];

// Bucket midpoints (px) for the 8-bucket potato-size histogram. Edges are
// [33,37,41,45,50,55,62]; the open-ended first/last buckets (<33, >62) use
// representative values, per the master potato-camera plan.
export const SIZE_BUCKET_MIDPOINTS = [31, 35, 39, 43, 47.5, 52.5, 58.5, 66];

// Inner bucket edges (px), as above; backend lib/potatoCamera.ts has the same.
export const SIZE_BUCKET_EDGES = [33, 37, 41, 45, 50, 55, 62];

/** "<33 px", "33–37 px", …, ">62 px" for size-histogram bucket `k`. */
export function sizeBucketLabel(k: number): string {
  const edges = SIZE_BUCKET_EDGES;
  if (k <= 0) return `<${edges[0]} px`;
  if (k >= edges.length) return `>${edges[edges.length - 1]} px`;
  return `${edges[k - 1]}–${edges[k]} px`;
}

// meanSizeFromHist returns the count-weighted mean potato size (px) for one
// point's histogram, or null when the histogram is absent/empty.
export function meanSizeFromHist(hist: number[] | null): number | null {
  if (!hist || hist.length === 0) return null;
  let weighted = 0;
  let total = 0;
  for (let k = 0; k < hist.length; k++) {
    const count = hist[k] ?? 0;
    const mid =
      SIZE_BUCKET_MIDPOINTS[k] ??
      SIZE_BUCKET_MIDPOINTS[SIZE_BUCKET_MIDPOINTS.length - 1];
    weighted += mid * count;
    total += count;
  }
  if (total <= 0) return null;
  return weighted / total;
}

/**
 * Per-point potato-camera values as the charts plot them. Counts are
 * per-interval sums, so the cumulative first-frame dump is dropped (each count
 * against its own threshold) or one 30k spike scales the whole Y axis; size is
 * the histogram mean (px). Shared by the Tracks Details panel and
 * Analytics → Weight Tracking, so both draw the same lines.
 */
export function cameraChartValues(statistics: DeviceStatisticsResponse): {
  potato: (number | null)[];
  stone: (number | null)[];
  size: (number | null)[];
} {
  const potatoCounts = statistics.getPotatoCountList();
  const stoneCounts = statistics.getStoneCountList();
  const potatoDump = cameraDumpThreshold(potatoCounts);
  const stoneDump = cameraDumpThreshold(stoneCounts);
  const clamp = (v: number | null, dump: number): number | null =>
    v == null || v > dump ? null : v;
  return {
    potato: potatoCounts.map((v) => clamp(v ?? null, potatoDump)),
    stone: stoneCounts.map((v) => clamp(v ?? null, stoneDump)),
    size: statistics.getSizeHistList().map(meanSizeFromHist),
  };
}

// rampColor maps an absolute value onto a sequential palette across [min, max].
function rampColor(pallet: string[], value: number, min: number, max: number): string {
  if (max <= min) return pallet[0];
  let t = (value - min) / (max - min);
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  return pallet[Math.round(t * (pallet.length - 1))];
}

// Same mapping as rampColor, but blending between neighbouring stops instead of
// snapping to the nearest one. Grain moisture varies by a few percentage points
// across a field, so five hard stops would paint most of a track flat.
function hexToRgb(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

function rampColorSmooth(pallet: string[], value: number, min: number, max: number): string {
  if (max <= min) return pallet[0];
  let t = (value - min) / (max - min);
  if (t < 0) t = 0;
  else if (t > 1) t = 1;

  const pos = t * (pallet.length - 1);
  const index = Math.min(Math.floor(pos), pallet.length - 2);
  const frac = pos - index;
  const lo = hexToRgb(pallet[index]);
  const hi = hexToRgb(pallet[index + 1]);
  const mix = (k: number) => Math.round(lo[k] + (hi[k] - lo[k]) * frac);

  return `rgb(${mix(0)}, ${mix(1)}, ${mix(2)})`;
}

export const potatoColor = (value: number, max: number): string =>
  rampColor(SENSOR_PALLET, value, 0, max);
export const notPotatoColor = (value: number, max: number): string =>
  rampColor(NOT_POTATO_PALLET, value, 0, max);
export const sizeColor = (value: number, min: number, max: number): string =>
  rampColor(SENSOR_PALLET, value, min, max);
export const moistureColor = (value: number, min: number, max: number): string =>
  rampColorSmooth(MOISTURE_PALLET, value, min, max);
export const fuelColor = (value: number, min: number, max: number): string =>
  rampColorSmooth(FUEL_PALLET, value, min, max);

// normalizeSensor returns normalized sensor value in the range for 0 to 12.
export function normalizeSensor(norm: number): number {
  return Math.round(norm * (SENSOR_PALLET.length - 1));
}

// Color bands a sensor ribbon is cut into. computeTrackFeature closes a polygon
// whenever the value changes, and a John Deere t/ha norm changes on almost
// every 1 Hz point — on the old floor(norm * 100) that was 30-40k polygons per
// device-day. 20 bands of 5 on the 0..100 scale keep the ramp readable.
const SENSOR_COLOR_BANDS = 20;

/**
 * The 0..100 value a swath segment is colored with: the lower edge of the
 * point's band. A saturated reading (norm 1) stays at 100, the ramp's end.
 */
export function sensorBandValue(norm: number): number {
  const band = Math.floor(norm * SENSOR_COLOR_BANDS);
  return Math.min(Math.max(band, 0), SENSOR_COLOR_BANDS) * (100 / SENSOR_COLOR_BANDS);
}

/**
 * Header width at each shifted point, metres: the response's own `widthM` (the
 * width timeline at that fix's time, sent with ?yield=1) when it has one, else
 * the device's width. Using today's width for every day drew the days before a
 * header change too wide or too narrow.
 */
function pointWidths(
  response: DeviceStatisticsResponse,
  points: Array<DataPoint>,
  fallback: number,
): (k: number) => number {
  const widths = response.getWidthList();
  return (k: number) => {
    const w = widths[points[k].gps_index];
    return w != null && w > 0 ? w : fallback;
  };
}


// Returns the direction of the point c relative to the vector a -> b.
//     1 if c is ccw (left) from p1->p2,
//    -1 if c is cw (right) from p1->p2,
//     0 if c is colinear with p1->p2
function signedArea(a: number[], b: number[], c: number[]): number {
  const dx1 = b[0] - a[0];
  const dy1 = b[1] - a[1];
  const dx2 = c[0] - b[0];
  const dy2 = c[1] - b[1];

  return dx1 * dy2 - dx2 * dy1;
}

// Returns orientation of the polygon
//     1 if is ccw (left) oriented
//    -1 if is cw (right) oriented
//     0 if is colinear
function polygonOrientation(poly: Array<Position>): number {
  if (poly.length < 3) {
    return 0;
  }

  let area: number = 0;
  for (let i = 1; i < poly.length - 1; i++) {
    area += signedArea(poly[i - 1], poly[i], poly[i + 1]);
  }

  return Math.sign(area);
}

function expandBBox(box: BBox | undefined, p: Position): BBox {
  if (!box) {
    return [p[0], p[1], p[0], p[1]];
  }

  box[0] = Math.min(box[0], p[0]);
  box[1] = Math.min(box[1], p[1]);
  box[2] = Math.max(box[2], p[0]);
  box[3] = Math.max(box[3], p[1]);

  return box;
}

function smoothLine(src: Array<Position>, level: number): Array<Position> {
  if (level <= 1) {
    return src
  }

  const dst = Array.from(src);

  for (var lvl = 0; lvl < level; lvl++) {
    const tmp = Array.from(dst);

    for (var i = 1; i < dst.length; i++) {
      const v1 = tmp[i - 1];
      const v2 = tmp[i];


      dst[i - 1] = [
        0.75 * v1[0] + 0.25 * v2[0],
        0.75 * v1[1] + 0.25 * v2[1],
      ];
      dst[i] = [
        0.25 * v1[0] + 0.75 * v2[0],
        0.25 * v1[1] + 0.75 * v2[1],
      ];
    }
  }

  return dst
}

// computeBearings computes bearing for each point
function computeBearings(line: Array<Position>): Array<number> {
  const bearings = new Array<number>(line.length);

  if (line.length >= 2) {
    for (let i = 0; i < line.length - 1; i++) {
      bearings[i] = bearing(line[i], line[i + 1]);
    }

    bearings[line.length - 1] = bearing(
      line[line.length - 2], line[line.length - 1], { final: true });
  }

  return bearings;
}

/**
 * Check if a GPS coordinate is valid (not 0,0 ocean point, not null island).
 */
export function isValidCoord(lng: number, lat: number): boolean {
  // Filter exact 0,0 (null island) and near-zero coords that are clearly invalid
  if (Math.abs(lng) < 0.001 && Math.abs(lat) < 0.001) return false;
  // Filter out-of-range coordinates
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  return true;
}

/**
 * Compute the geographic median center of a set of points (simple median of lat/lng).
 * More robust to outliers than arithmetic mean.
 */
function medianCenter(points: Array<[number, number]>): [number, number] | null {
  if (points.length === 0) return null;
  const lngs = points.map((p) => p[0]).sort((a, b) => a - b);
  const lats = points.map((p) => p[1]).sort((a, b) => a - b);
  const mid = Math.floor(points.length / 2);
  return [lngs[mid], lats[mid]];
}

/**
 * Build a Set of indices that are GPS outliers.
 * An outlier is a point that is > MAX_DISTANCE_FROM_MEDIAN_KM from the median center
 * of all valid points, or has invalid coordinates (0,0), or insufficient satellites.
 */
function computeOutlierIndices(
  response: DeviceStatisticsResponse,
  stats?: TrackDropStats,
): Set<number> {
  const { decode } = precision(10);
  const length = response.getLength();
  const coords = response.getCoordsList();
  const satellites = response.getSatQtyList();
  const minSatellites = minSatellitesOf(response, stats);
  const times = response.getTimeList();
  const outliers = new Set<number>();

  // First pass: collect valid points and mark obviously invalid ones
  const validPoints: Array<[number, number]> = [];
  const validIndices: number[] = [];

  for (let i = 0; i < length; i++) {
    const lng = decode(coords[i * 2]);
    const lat = decode(coords[i * 2 + 1]);

    if (!isValidCoord(lng, lat)) {
      if (stats) stats.invalidCoord++;
      outliers.add(i);
      continue;
    }
    if (satellites[i] < minSatellites) {
      if (stats) stats.belowSatFloor++;
      outliers.add(i);
      continue;
    }
    validPoints.push([lng, lat]);
    validIndices.push(i);
  }

  // Second pass: find points too far from the median center
  if (validPoints.length > 2) {
    const center = medianCenter(validPoints);
    if (center) {
      const centerPt = point(center);
      for (let k = 0; k < validPoints.length; k++) {
        const d = distance(point(validPoints[k]), centerPt, { units: "kilometers" });
        if (d > MAX_DISTANCE_FROM_MEDIAN_KM) {
          if (stats && !outliers.has(validIndices[k])) stats.farFromMedian++;
          outliers.add(validIndices[k]);
        }
      }
    }
  }

  // Third pass: reject fixes only reachable at an impossible speed. Walked over
  // the survivors of the passes above, so a jump is measured against the last
  // believable position rather than against another spike.
  let previous: { position: [number, number]; time: number } | null = null;
  for (let k = 0; k < validIndices.length; k++) {
    const i = validIndices[k];
    if (outliers.has(i)) continue;

    const position = validPoints[k];
    const time = times[i];

    if (previous !== null) {
      const hours = (time - previous.time) / 3600;
      if (hours > 0) {
        const km = distance(point(previous.position), point(position), { units: "kilometers" });
        if (km / hours > MAX_IMPLIED_SPEED_KMH) {
          if (stats) stats.speedJump++;
          outliers.add(i);
          // Keep `previous` — the jump is the suspect, not the anchor.
          continue;
        }
      }
    }

    previous = { position, time };
  }

  return outliers;
}

// DataPoint is an intermediate representation of the single point on the
// track after shift is done.
type DataPoint = {
  // GPS time (time when positional data was collected).
  time: number,
  // GPS location.
  point: Position,
  // Time when sensor data was collected
  sensor_time: number,
  // Normalized sensor value [0 .. 1]
  sensor_value: number,
  // Index into the response's per-point arrays that this GPS position was
  // paired with by the delay shift. Camera counts describe the crop passing the
  // camera, so they take the same alignment as the sensor reading.
  value_index: number,
  // Index of the GPS fix itself — its speed belongs to the position, not to
  // the delayed sensor reading.
  gps_index: number,
};

/**
 * Display filter for a track: whether the point at a GPS position is drawn.
 * `sensor` is the delay-shifted normalized reading [0 .. 1] (null for builders
 * that do not use it), `speedKmh` the fix's own speed, `turnRadiusM` the fix's
 * turn radius (null = unknown, or not computed because `removesTurns` is off).
 *
 * A rejected point BREAKS the ribbon rather than being skipped: skipping would
 * bridge its neighbours into one polygon across ground the filter hid.
 */
export type TrackPointKeep = ((
  sensor: number | null,
  speedKmh: number,
  turnRadiusM: number | null,
) => boolean) & {
  /** Set when the filter hides headland turns — only then are radii computed. */
  removesTurns?: boolean;
};

/**
 * Turn radius per response index, or null when `keep` does not remove turns.
 *
 * Measured over the clean GPS fixes only (an outlier would read as a hairpin)
 * and before any filter, as analytics does: geometry is how the machine drove,
 * and a stream already punched full of holes would invent turns.
 */
function turnRadiiFor(
  response: DeviceStatisticsResponse,
  outliers: Set<number>,
  keep?: TrackPointKeep,
): Array<number | null> | null {
  if (!keep?.removesTurns) return null;
  const { decode } = precision(10);
  const coords = response.getCoordsList();
  const length = response.getLength();
  const clean: number[] = [];
  for (let i = 0; i < length; i++) if (!outliers.has(i)) clean.push(i);
  const radii = turnRadiiMeters(
    clean.map((i) => [decode(coords[i * 2]), decode(coords[i * 2 + 1])] as const),
  );
  const out: Array<number | null> = new Array(length).fill(null);
  clean.forEach((row, k) => (out[row] = radii[k]));
  return out;
}

function trackShift(
  dst: Array<DataPoint>,
  response: DeviceStatisticsResponse,
  delay: number,
  max_dev: number,
  outliers?: Set<number>,
) {
  const { decode } = precision(10);

  const length = response.getLength();
  const times = response.getTimeList();
  const sensor = response.getNormList();
  const coords = response.getCoordsList();

  if (length === 0) {
    console.debug("Track is empty - nothing to shift");
    return;
  }

  const isOutlier = (idx: number) => outliers != null && outliers.has(idx);

  if (delay < 1) {
    // If delay is less then 1 than shift is does not matter.
    for (let i = 0; i < length; i++) {
      if (isOutlier(i)) continue;
      dst.push({
        time: times[i],
        point: [ decode(coords[i * 2]), decode(coords[i * 2 + 1]) ],
        sensor_time: times[i],
        sensor_value: sensor[i],
        value_index: i,
        gps_index: i,
      });
    }

    return;
  }

  let gps_cursor    = 0;
  let sensor_cursor = gps_cursor + 1;

  for (; sensor_cursor < length; sensor_cursor++, gps_cursor++) {
    const diff_seconds = times[sensor_cursor] - times[gps_cursor];
    const dev          = diff_seconds - delay;

    if (Math.abs(dev) > max_dev) {
      // Attempting to fix interval between the points

      gps_cursor    = (gps_cursor === 0) ? 0 : gps_cursor + 1;
      sensor_cursor = gps_cursor + 1;

      while (sensor_cursor < length) {
        const diff_seconds = times[sensor_cursor] - times[gps_cursor];
        if (diff_seconds >= delay) {
          // @note: Checking if sensor_time of last point in the dst array
          // is past of the current sensor point to prevent reuse of the same
          // sensor value multiple times.
          if (dst.length > 0 && dst[dst.length - 1].sensor_time >= times[sensor_cursor]) {
            gps_cursor++;
            continue;
          }

          // Otherwise breaking the loop and attempting to shift rest of the
          // track with given distance between the points that is equal to
          // sensor_cursor - gps_cursor.
          break;
        }
        sensor_cursor++;
      }

      if (sensor_cursor >= length) {
        return;
      }
    }

    // Skip GPS outlier points
    if (isOutlier(gps_cursor)) continue;

    dst.push({
      time: times[gps_cursor],
      point: [ decode(coords[gps_cursor * 2]), decode(coords[gps_cursor * 2 + 1]) ],
      sensor_time: times[sensor_cursor],
      sensor_value: sensor[sensor_cursor],
      value_index: sensor_cursor,
      gps_index: gps_cursor,
    });
  }
}

function makeRing(line: Array<Position>, bearings: Array<number>, width: number): Array<Position> {
  const ring = new Array<Position>();

  // Collect points form the other size of the track
  for (let i = 0; i < line.length; i++) {
    const f = destination(line[i], width * 0.5, bearings[i] + 90.0, { units: "meters" });
    ring.push(f.geometry.coordinates);
  }

  // Collect points from the one side of the track
  for (let i = line.length - 1; i >= 0; i--) {
    const f = destination(line[i], width * 0.5, bearings[i] - 90.0, { units: "meters" });
    ring.push(f.geometry.coordinates);
  }

  if (polygonOrientation(ring) === -1) {
    ring.reverse();
  }

  ring.push(ring[0]);

  return ring;
}

function computeTrackFeature(
  response: DeviceStatisticsResponse,
  width: number,
  delay: number,
  from?: Date,
  to?: Date,
  stats?: TrackDropStats,
  keep?: TrackPointKeep,
): FeatureCollection<Polygon> {

  console.time("track-shift");
  const outliers = computeOutlierIndices(response, stats);
  const points = new Array<DataPoint>();
  trackShift(points, response, delay, 2, outliers);
  console.timeEnd("track-shift");

  if (stats) {
    stats.points = points.length;
    stats.widthM = width;
    stats.delayS = delay;
  }

  // No GPS-valid points (e.g. static device at 0,0 or sat filter) — cannot build map geometry.
  if (points.length === 0) {
    return {
      type: "FeatureCollection",
      features: [],
    };
  }

  const line      = smoothLine(points.map((p) => p.point), 10);
  const bearings  = computeBearings(line);
  const unix_from = from ? moment(from).unix() : null;
  const unix_to   = to ? moment(to).unix() : null;

  let cursor = 0;
  if (unix_from !== null) {
    // Skip all the points before unix_from
    while (cursor < points.length && points[cursor].time < unix_from) {
      cursor++;
    }
  }

  if (cursor > points.length) {
    // Noting left to return
    return {
      type: "FeatureCollection",
      features: [],
    };
  }

  // Bounding box of the selectedtrack
  let bbox: BBox | undefined;
  // Track segments
  const features = new Array<Feature<Polygon>>();

  const speeds = keep ? response.getSpeedList() : null;
  const radii = turnRadiiFor(response, outliers, keep);
  const kept = (i: number): boolean =>
    !keep ||
    keep(
      points[i].sensor_value,
      speeds![points[i].gps_index] ?? 0,
      radii?.[points[i].gps_index] ?? null,
    );
  // Sentinel no reading can equal, so the first kept point after a filtered
  // run always starts a fresh segment with its own value.
  const FILTERED = -1;
  const widthAt = pointWidths(response, points, width);

  let segment_value = sensorBandValue(points[0].sensor_value);
  let segment_start = 0; // index of the point where current segment starts
  if (!kept(0)) {
    segment_start = 1;
    segment_value = FILTERED;
  }

  const finalize_segment = (end: number, value: number) => {
    if (end <= segment_start) {
      segment_value = value;
      return;
    }

    features.push({
      type: "Feature",
      geometry: { 
        type: "Polygon",
        coordinates: [
           makeRing(
            line.slice(segment_start, end + 1),
            bearings.slice(segment_start, end + 1),
            widthAt(segment_start)
          )
        ]
      },
      // Mid-segment GPS time, for colouring a day by time of pass.
      properties: { sensor: segment_value, t: (points[segment_start].time + points[end].time) / 2 }
    })

    segment_start = end;
    segment_value = value;
  };

  let i = 1;
  for (; i < points.length; i++) {
    if (unix_to != null && points[i].time > unix_to) {
      break;
    }

    if (unix_from != null && points[i].time < unix_from) {
      segment_start++;
      continue;
    }

    if (!kept(i)) {
      // Close the run before the filtered point and restart after it.
      finalize_segment(i - 1, FILTERED);
      segment_start = i + 1;
      continue;
    }

    bbox = expandBBox(bbox, points[i].point);

    const val = sensorBandValue(points[i].sensor_value);
    const gap = points[i].time - points[i - 1].time;

    if (gap >= segmentGapSeconds(delay)) {
      finalize_segment(i - 1, val);
      // @important: moving cursor forward to create visible gap in the track.
      segment_start = i;
    } else if (
      val !== segment_value ||
      (i - segment_start) > 20 ||
      // A header change: the next segment is drawn at the new width.
      widthAt(i) !== widthAt(segment_start)
    ) {
      finalize_segment(i, val);
    }
  }

  finalize_segment(i - 1, 0);

  return {
    type: "FeatureCollection",
    features: features,
    bbox: bbox,
  }
}

// forEachPoint iterates through the response data converting points to
// the geojson respresentation.
// Returns bounding box that contains every point of the data or undefined
// if response is empty.
function forEachPoint(
  response: DeviceStatisticsResponse,
  from: Date | undefined,
  to: Date | undefined,
  fn: (position: Position, index: number) => boolean,
  outliers?: Set<number>,
): BBox | undefined {
  if (response.getLength() === 0) {
    return undefined;
  }

  // TODO(nk2ge5k): it would be nice to know correct precision, may be I should
  // return it from the server for clarity.
  const { decode } = precision(10);

  const times = response.getTimeList();
  const coords = response.getCoordsList();
  const satelites = response.getSatQtyList();
  const minSatellites = minSatellitesOf(response);

  const unix_from = from ? moment(from).unix() : null;
  const unix_to = to ? moment(to).unix() : null;

  // Bounding box that contains entire track
  let bbox: BBox | undefined;

  // Cicle through every point of the statistics and create lines.
  for (let i = 0; i < response.getLength(); i++) {
    if (i > 0 && times[i] === times[i - 1]) {
      continue;
    }

    if (unix_from !== null && unix_to !== null && unix_from <= unix_to) {
      if (times[i] < unix_from || unix_to < times[i]) {
        continue;
      }
    }

    // Index of the current point in coords array. Length of the coords array
    // must be exactly two times greater then response.length.
    const j = i * 2;
    // Integer representation of the device
    const ilng = coords[j];
    const ilat = coords[j + 1];

    // Checking if data was gathered with sufficient accuracy.
    // We dont want to show track that is going all over the place - sometimes
    // GPS can send coordinates of 0,0.
    // The coordinates 0,0 in the ocean so we can safetly assume that it is in
    // fact invalid coordinates.
    if (satelites[i] < minSatellites || (ilng === 0 && ilat === 0)) {
      continue;
    }

    // Skip distance-based outliers
    if (outliers && outliers.has(i)) {
      continue;
    }

    const lng = decode(ilng);
    const lat = decode(ilat);

    if (fn([lng, lat], i)) {
      if (bbox === undefined) {
        // Initializing bounding box with givien coordinates.
        bbox = [lng, lat, lng, lat];
      } else {
        // Expanding bounding box if needed
        bbox[0] = Math.min(bbox[0], lng);
        bbox[1] = Math.min(bbox[1], lat);
        bbox[2] = Math.max(bbox[2], lng);
        bbox[3] = Math.max(bbox[3], lat);
      }
    }
  }

  return bbox;
};


// sensorColor returns color of the normalizeSensor value
export const sensorColor = (value: number): string => {
  if (value < 0) {
    value = 0;
  } else if (value > SENSOR_PALLET.length - 1) {
    value = SENSOR_PALLET.length - 1;
  }
  return SENSOR_PALLET[value];
}

// sensorFeatures resturns GeoJSON FeatureCollection with lines colored
// according to the sensor value of the device.
// TODO:
// - width 
// - delay
function sensorFeatures(
  response: DeviceStatisticsResponse,
  width: number,
  delay: number,
  from?: Date,
  to?: Date,
  // Drop a track whose whole extent is under 100 m. That is a machine parked
  // with the sensor running, and the blob of GPS jitter it paints is noise, not
  // harvest. Only devices flagged `is_static_device` get this treatment: a real
  // machine working a small plot produces exactly the same tiny extent, and
  // blanking it left the map empty while the admin Metrics view — which always
  // passes a time range, so it never reached this check — drew the track fine.
  hideStationary: boolean = false,
  // Out-param: when given, every stage that can drop a fix records its tally
  // here so the caller can log why a track came back empty.
  stats: TrackDropStats = newDropStats(),
  keep?: TrackPointKeep,
): FeatureCollection<Polygon> {
  stats.length = response.getLength();
  stats.widthM = width;
  stats.delayS = delay;

  if (response.getLength() === 0) {
    return {
      type: "FeatureCollection",
      features: [],
    };
  }

  const result = computeTrackFeature(response, width, delay, from, to, stats, keep);
  stats.features = result.features.length;

  if (result.bbox) {
    stats.bboxDiagonalM = Math.round(distance(
      point([result.bbox[0], result.bbox[1]]),
      point([result.bbox[2], result.bbox[3]]),
      { units: "meters" }
    ));
  }

  if (hideStationary && result.bbox && !from && !to && stats.bboxDiagonalM < 100) {
    stats.stationaryDropped = true;
    stats.features = 0;
    return {
      type: "FeatureCollection",
      features: [],
    };
  }

  return result;
}

/**
 * One-line verdict on why a track did or did not draw. Logged per device on
 * every paint: a blank map is always one of these numbers going to zero, and
 * reading it off the console beats bisecting the pipeline by hand.
 */
export function explainTrack(label: string, stats: TrackDropStats): void {
  const kept = stats.length - stats.invalidCoord - stats.belowSatFloor
    - stats.farFromMedian - stats.speedJump;

  let verdict: string;
  if (stats.features > 0) verdict = "OK — drawn";
  else if (stats.length === 0) verdict = "EMPTY — the API returned no points for this day";
  else if (kept <= 0) verdict = "BLANK — every fix was filtered out (see the drop counts)";
  else if (stats.points === 0) verdict = `BLANK — the delay shift (${stats.delayS}s) consumed every point`;
  else if (stats.stationaryDropped) verdict = `BLANK — track extent ${stats.bboxDiagonalM}m is under the 100m stationary cut-off`;
  else verdict = "BLANK — geometry built but produced no polygons";

  const line = `[Tracks] ${label}: ${verdict}`;
  const detail = {
    pointsFromApi: stats.length,
    droppedInvalidCoord: stats.invalidCoord,
    droppedBelowSatFloor: stats.belowSatFloor,
    droppedFarFromMedian: stats.farFromMedian,
    droppedSpeedJump: stats.speedJump,
    keptAfterFilters: kept,
    afterDelayShift: stats.points,
    polygons: stats.features,
    extentMetres: stats.bboxDiagonalM,
    minSatellitesCount: stats.satFloor,
    satFloorIgnored: stats.satFloorIgnored,
    headerWidthM: stats.widthM,
    delayS: stats.delayS,
  };

  // A drawn track is only interesting when something else is wrong, so it goes
  // to debug (hidden unless the console is set to Verbose). A blank one warns.
  if (stats.features > 0) console.debug(line, detail);
  else console.warn(line, detail);
}

export const speedColor = (value: number, max?: number): string => {
  max = max ?? 30;

  if (value < 0) {
    value = 0;
  } else if (value > max) {
    value = max;
  }

  const idx = Math.round(value / max * (SPEED_PALLET.length - 1));

  return SPEED_PALLET[idx];
}

// speedFeatures resturns GeoJSON FeatureCollection with lines colored
// according to the speed  of the device.
function speedFeatures(
  response: DeviceStatisticsResponse,
  from?: Date,
  to?: Date,
  keep?: TrackPointKeep,
): FeatureCollection<LineString> {

  if (response.getLength() === 0) {
    return {
      type: "FeatureCollection",
      features: [],
    };
  }

  const speed = response.getSpeedList();
  const times = response.getTimeList();
  const outliers = computeOutlierIndices(response);
  const radii = turnRadiiFor(response, outliers, keep);

  // List of features that will end up in the result.
  let features: Feature<LineString>[] = [];
  // Current line
  let part: Position[] = [];
  // Value of the previous point in the line.
  let previous_value: number | null = null;
  // GPS time of the current line's first point.
  let part_time: number | null = null;
  // Index of the last point
  // NOTE(nk2ge5k): This is needed because forEachPoint reomves some of the
  // points and does not pass them to the caller, therfore index-1 is not
  // the same as the previous index.
  let previous_index: number = 0;

  // finishPart is a helper function that adds current part of the line
  // to the list of features and start new line.
  const finishPart = () => {
    if (part.length >= 2) {
      features.push({
        type: "Feature",
        properties: { "line-color": speedColor(previous_value!), t: part_time },
        geometry: {
          type: "LineString",
          coordinates: part,
        }
      });
    }

    part = [];
  };

  const bbox = forEachPoint(response, from, to, (position: Position, index: number): boolean => {
    const value = speed[index];
    if (keep && !keep(null, value, radii?.[index] ?? null)) {
      // Break the line here so it never runs across the hidden stretch.
      finishPart();
      previous_value = null;
      return false;
    }
    if (previous_value !== null) {
      if (Math.abs(times[index] - times[previous_index]) > MAX_DURATION_BETWEEN_POINTS) {
        // If to much time passed between two points - separating the line.
        finishPart();
      } else if (part.length > 0 && distance(position, part[part.length - 1]) > 1) {
        return false;
      } else if (value !== previous_value) {
        // If value changed - separating the line but connecting them because
        // otherwise resulting line would be chopped.
        part.push(position);
        finishPart();
      }
    }

    previous_value = value;
    previous_index = index;
    if (part.length === 0) part_time = times[index];
    part.push(position);

    return true;
  }, outliers);

  if (part.length > 0) {
    finishPart();
  }

  return {
    type: "FeatureCollection",
    features: features,
    bbox: bbox,
  };
}

// metricSwathFeatures builds harvester-width Polygon segments colored by any
// per-point metric — the camera counts, mean potato size, or grain moisture.
//
// It deliberately mirrors computeTrackFeature rather than speedFeatures: these
// readings describe the crop actually going through the machine, so they need
// the same delay shift, smoothing and swath geometry the sensor layer gets.
// A null value (nothing measured at that point) or a cumulative dump BREAKS the
// run so nothing is drawn across ground the metric says nothing about.
function metricSwathFeatures(
  response: DeviceStatisticsResponse,
  values: (number | null)[],
  colorFor: (value: number) => string,
  width: number,
  delay: number,
  dumpThreshold: number,
  from?: Date,
  to?: Date,
  keep?: TrackPointKeep,
): FeatureCollection<Polygon> {
  if (response.getLength() === 0) {
    return { type: "FeatureCollection", features: [] };
  }

  const outliers = computeOutlierIndices(response);
  const points = new Array<DataPoint>();
  trackShift(points, response, delay, 2, outliers);

  if (points.length === 0) {
    return { type: "FeatureCollection", features: [] };
  }

  const line      = smoothLine(points.map((p) => p.point), 10);
  const bearings  = computeBearings(line);
  const unix_from = from ? moment(from).unix() : null;
  const unix_to   = to ? moment(to).unix() : null;

  const speeds = keep ? response.getSpeedList() : null;
  const radii = turnRadiiFor(response, outliers, keep);
  // Camera value paired with point i, or null when there is nothing usable —
  // a filtered point is "nothing usable", so it breaks the run like a gap.
  const valueAt = (i: number): number | null => {
    const gi = points[i].gps_index;
    if (keep && !keep(null, speeds![gi] ?? 0, radii?.[gi] ?? null)) return null;
    const v = values[points[i].value_index];
    if (v == null || v > dumpThreshold) return null;
    return v;
  };

  let bbox: BBox | undefined;
  const features = new Array<Feature<Polygon>>();
  const widthAt = pointWidths(response, points, width);

  let segment_start = 0;
  let segment_value = valueAt(0);

  const finalize_segment = (end: number, next: number | null) => {
    if (end > segment_start && segment_value != null) {
      features.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [
            makeRing(
              line.slice(segment_start, end + 1),
              bearings.slice(segment_start, end + 1),
              widthAt(segment_start),
            ),
          ],
        },
        properties: {
          value: segment_value,
          metricColor: colorFor(segment_value),
          t: (points[segment_start].time + points[end].time) / 2,
        },
      });
    }

    segment_start = end;
    segment_value = next;
  };

  let i = 1;
  for (; i < points.length; i++) {
    if (unix_to != null && points[i].time > unix_to) {
      break;
    }

    if (unix_from != null && points[i].time < unix_from) {
      segment_start++;
      segment_value = valueAt(i);
      continue;
    }

    bbox = expandBBox(bbox, points[i].point);

    const val = valueAt(i);
    const gap = points[i].time - points[i - 1].time;

    if (gap >= segmentGapSeconds(delay) || val == null || segment_value == null) {
      // Time gap, or no camera coverage on one side of this step — close the
      // run and restart at i so the break stays visible.
      finalize_segment(i - 1, val);
      segment_start = i;
    } else if (
      val !== segment_value ||
      (i - segment_start) > 20 ||
      widthAt(i) !== widthAt(segment_start)
    ) {
      finalize_segment(i, val);
    }
  }

  finalize_segment(i - 1, null);

  return {
    type: "FeatureCollection",
    features: features,
    bbox: bbox,
  };
}

// The camera first-frame dump rule lives in utils/cameraDump (shared with the admin panel).

// maxOf / minMaxOf compute the color-scale range from a device's own values
// (per-device scaling, consistent with how speed is colored), ignoring dumps.
function maxOf(values: (number | null)[], dumpThreshold: number): number {
  let max = 0;
  for (const v of values) {
    if (v != null && v <= dumpThreshold && v > max) max = v;
  }
  return max;
}

function minMaxOf(
  values: (number | null)[],
  dumpThreshold: number,
): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (v == null || v > dumpThreshold) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!isFinite(min) || !isFinite(max)) return { min: 0, max: 0 };
  return { min, max };
}

// potatoFeatures / notPotatoFeatures / sizeFeatures color the track by the
// respective camera metric. Absolute scale, per-device data range, drawn at the
// harvester width like the sensor layer.
function potatoFeatures(
  response: DeviceStatisticsResponse,
  width: number,
  delay: number,
  from?: Date,
  to?: Date,
  keep?: TrackPointKeep,
): FeatureCollection<Polygon> {
  const values = response.getPotatoCountList();
  const dump = cameraDumpThreshold(values);
  const max = maxOf(values, dump);
  return metricSwathFeatures(
    response, values, (v) => potatoColor(v, max), width, delay, dump, from, to, keep);
}

function notPotatoFeatures(
  response: DeviceStatisticsResponse,
  width: number,
  delay: number,
  from?: Date,
  to?: Date,
  keep?: TrackPointKeep,
): FeatureCollection<Polygon> {
  const values = response.getStoneCountList();
  const dump = cameraDumpThreshold(values);
  const max = maxOf(values, dump);
  return metricSwathFeatures(
    response, values, (v) => notPotatoColor(v, max), width, delay, dump, from, to, keep);
}

function sizeFeatures(
  response: DeviceStatisticsResponse,
  width: number,
  delay: number,
  from?: Date,
  to?: Date,
  keep?: TrackPointKeep,
): FeatureCollection<Polygon> {
  // Mean size is an average, not a running total, so the cumulative first-frame
  // dump never distorts it — the threshold below is effectively inert here.
  const values = response.getSizeHistList().map(meanSizeFromHist);
  const dump = cameraDumpThreshold(values);
  const { min, max } = minMaxOf(values, dump);
  return metricSwathFeatures(
    response, values, (v) => sizeColor(v, min, max), width, delay, dump, from, to, keep);
}

// Narrowest moisture span (percentage points) the ramp is stretched over. A
// machine standing still reports the same reading all day; without a floor, its
// half-point of sensor noise would be spread across the whole palette and read
// as a dramatic wet-to-dry gradient.
const MOISTURE_MIN_SPAN = 5;

/**
 * The [lo, hi] the moisture ramp is stretched over for these readings. One
 * owner, so the map, its legend and the Details chart paint a reading the same
 * color. Values are rounded to whole percent first, as the map draws them.
 */
export function moistureScale(moistures: (number | null)[]): { lo: number; hi: number } {
  const values = moistures.map((v) => (v == null ? null : Math.round(v)));
  const { min, max } = minMaxOf(values, 100);
  const span = Math.max(max - min, MOISTURE_MIN_SPAN);
  const lo = Math.max((min + max - span) / 2, 0);
  return { lo, hi: lo + span };
}

// moistureFeatures colors the track by the grain-moisture sensor.
//
// Ramped across the selection's own range rather than the sensor's full 0..100,
// for the same reason the camera metrics are: a wheat harvest lives inside a
// few percentage points, and an absolute scale would paint the entire field one
// flat color. Points where nothing was measured are already null (see
// DeviceStatisticsResponse), so the swath breaks over them.
function moistureFeatures(
  response: DeviceStatisticsResponse,
  width: number,
  delay: number,
  from?: Date,
  to?: Date,
  keep?: TrackPointKeep,
): FeatureCollection<Polygon> {
  // Rounded to whole percent: metricSwathFeatures starts a new polygon every
  // time the value changes, and raw floats would give one polygon per fix.
  const moistures = response.getMoistureList();
  const values = moistures.map((v) => (v == null ? null : Math.round(v)));
  const { lo, hi } = moistureScale(moistures);

  return metricSwathFeatures(
    response, values, (v) => moistureColor(v, lo, hi), width, delay, 100, from, to, keep);
}

// Narrowest fuel span (mV) the ramp is stretched over. The input wobbles about
// ±12 mV with the tank untouched; without a floor that noise would paint a
// parked machine across the whole palette.
const FUEL_MIN_SPAN = 100;
// Fuel readings are drawn in 10 mV steps: metricSwathFeatures starts a new
// polygon whenever the value changes, and raw noise would change it every fix.
const FUEL_STEP_MV = 10;

/** The [lo, hi] the fuel ramp is stretched over — shared by map and chart. */
export function fuelScale(fuels: (number | null)[]): { lo: number; hi: number } {
  const { min, max } = minMaxOf(fuels, Infinity);
  const span = Math.max(max - min, FUEL_MIN_SPAN);
  const lo = Math.max((min + max - span) / 2, 0);
  return { lo, hi: lo + span };
}

// fuelFeatures colors the track by the fuel sensor input (mV), ramped across
// the selection's own range like moisture. Not delay-shifted: the delay is the
// grain/potato flow's travel time and has nothing to do with the tank.
function fuelFeatures(
  response: DeviceStatisticsResponse,
  width: number,
  from?: Date,
  to?: Date,
  keep?: TrackPointKeep,
): FeatureCollection<Polygon> {
  const fuels = response.getFuelMvList();
  const values = fuels.map((v) => (v == null ? null : Math.round(v / FUEL_STEP_MV) * FUEL_STEP_MV));
  const { lo, hi } = fuelScale(fuels);

  return metricSwathFeatures(
    response, values, (v) => fuelColor(v, lo, hi), width, 0, Infinity, from, to, keep);
}

export {
  sensorFeatures, speedFeatures, potatoFeatures, notPotatoFeatures, sizeFeatures,
  moistureFeatures, fuelFeatures,
};
